import Button from 'components/Button';
import Error from 'components/form/Error';
import Info from 'components/form/Info';
import Textarea from 'components/form/Inputs/Textarea';
import ActionModal from 'components/Modal/ActionModal';
import ProfileNavigationHead from 'components/profileNavigation/Head';
import Space from 'components/Space';
import UserInformationDisplay from 'components/UserInformation/UserInformationDisplay';
import { MentorshipStatusValues } from 'enums/MentorshipStatus';
import { FC, useContext, useState } from 'react';
import styled from 'styled-components';
import useSWR, { mutate } from 'swr';
import User from 'types/User';
import privateAPI from 'utils/privateAxiosInstance';
import { MentorshipManagementContext } from '..';

const ButtonContainer = styled.div`
  display: flex;
  width: fit-content;
  margin-top: 5rem;
  margin-bottom: 5rem;
`;

const Mentor: FC = () => {

  const [open, setOpen] = useState<boolean>(false);
  const [interruptionMotive, setInterruptionMotive] = useState<string>('');

  // États pour la proposition d'entretien
  const [openInterview, setOpenInterview] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [loadingInterview, setLoadingInterview] = useState<boolean>(false);
  const [interviewError, setInterviewError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string>('PHONE');

  // Fonction pour envoyer la demande au backend
  const handleProposeInterview = async () => {
    if (!selectedDate) {
      setInterviewError("Veuillez sélectionner une date.");
      return;
    }

    setLoadingInterview(true);
    setInterviewError(null);

    try {
      await privateAPI.post('/interview', {
        mentorshipId: mentorship?.id,
        date: new Date(selectedDate).toISOString(),
        type: selectedType,
      });
      
      setOpenInterview(false);
      // Optionnel : mutate('/mentorship/mentee/mentor-details') pour rafraîchir la page
    } catch (err: any) {
      setInterviewError(err.response?.data?.message || "Une erreur est survenue.");
    } finally {
      setLoadingInterview(false);
    }
  };

  const { data: mentor } = useSWR<User>('/mentorship/mentee/mentor-details');
  const { mentorship } = useContext(MentorshipManagementContext);
  const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setInterruptionMotive(e.target.value);
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [motiveError, setMotiveError] = useState<string | null>(null);
  const handleInterrupt = () => {
    if (!interruptionMotive.trim()) {
      setMotiveError("Ce champ est obligatoire.");
      return;
    }
    setMotiveError(null);

    setLoading(true);
    setError(false);
    privateAPI
      .post('/mentorship/mentee/interrupt-mentorship', { interruptionMotive })
      .then(() => {
        mutate('/mentorship/mentee');
        setOpen(false);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <ActionModal
        title="Demande d'interruption"
        color="error"
        open={open}
        close={() => setOpen(false)}
        apply={handleInterrupt}
        applyText="Demander l'interruption du mentorat"
        loading={loading}
      >
        <p>Vous êtes sur le point de demander l'interruption du mentorat.</p>
        <p>Cette demande sera examinée par un administrateur.</p>
        <p>
          Elle pourra donner lieu à l'interruption définitive du mentorat vous
          liant à {mentor?.firstName + ' ' + mentor?.lastName}, votre mentor
          actuel.
        </p>
        <Space height={0.5} />
        {error && <Error>Une erreur est survenue.</Error>}
        <Space height={0.5} />
        <Textarea
          value={interruptionMotive}
          onChange={handleChange}
          noEnterFireSubmit
          characterLimit={1000}
          placeholder="Quelle est la raison de votre demande ?"
          rows={10}
        />
        {motiveError && <Error>{motiveError}</Error>}
      </ActionModal>

      <ActionModal
        title="Proposer un entretien"
        color="primary"
        open={openInterview}
        close={() => {
          setOpenInterview(false);
          setInterviewError(null);
        }}
        apply={handleProposeInterview}
        applyText="Envoyer la proposition"
        loading={loadingInterview}
      >
        <p>
          Choisissez la date et le mode de rencontre pour votre entretien avec votre mentor.
        </p>

        <Space height={1} />

        {/* Champ Date et Heure */}
        <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.9rem', fontWeight: 'bold' }}>
          Date et heure
        </label>
        <input
          type="datetime-local"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            marginBottom: '1rem',
            fontFamily: 'inherit'
          }}
        />

        {/* Nouveau Champ : Type d'entretien (Valeurs DB) */}
        <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.9rem', fontWeight: 'bold' }}>
          Type d'entretien
        </label>
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            backgroundColor: 'white',
            fontFamily: 'inherit'
          }}
        >
          <option value="MEETJIT">Visioconférence (Jitsi)</option>
          <option value="PHONE">Téléphone</option>
          <option value="IRL">En personne (IRL)</option>
          <option value="OTHER">Autre</option>
        </select>

        {interviewError && (
          <>
            <Space height={1} />
            <Error>{interviewError}</Error>
          </>
        )}
      </ActionModal>

      <ProfileNavigationHead
        title="Informations générales"
        subtitle="Liste les informations générales de votre mentor"
      />
      {(
        ['FINISHED_ADMIN', 'FINISHED_DATE'] as MentorshipStatusValues[]
      ).includes(mentorship?.status as MentorshipStatusValues) && (
        <>
          <Info>
            Si vous ne l'avez pas encore fait, nous vous invitons à remplir
            votre bilan de fin de mentorat en suivant{' '}
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSdrLqipJAmMmMIAw8BfdTeV8xCM7Umw5j95ySZ2V_-RcHkWeQ/viewform?usp=sf_link"
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'underline' }}
            >
              ce lien
            </a>
            .
          </Info>
          <Space height={1} />
        </>
      )}
      <UserInformationDisplay variant="mentor" reader="mentee" user={mentor} />
      {mentorship?.status &&
        !(
          [
            'INTERRUPTION_REQUEST_MENTEE',
            'INTERRUPTION_REQUEST_MENTOR',
            'FINISHED_ADMIN',
            'FINISHED_DATE',
          ] as MentorshipStatusValues[]
        ).includes(mentorship.status) && (
          <ButtonContainer>
            <Button
              color="primary"
              onClick={() => setOpenInterview(true)}
              style={{ marginRight: '1rem' }}
            >
              Proposer un entretien
            </Button>

            <Button
              color="error"
              variant="outlined"
              onClick={() => setOpen(true)}
            >
              Demander l'interruption du mentorat
            </Button>
          </ButtonContainer>
        )}
    </>
  );
};

export default Mentor;
